import { useCallback, useRef } from "react";
import { Issue } from "@adsk/acc-issues-types";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueById } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";

/**
 * Hook that returns a function to get issue data by ID.
 * The returned function accepts an issueId and returns the issue data if found.
 * Uses a ref-based cache to maintain referential stability based on deep comparison.
 *
 * @returns A function that takes issueId and returns Issue | undefined
 */
export const useIssueData = (): ((
  issueId: string | undefined
) => Issue | undefined) => {
  const getIssue = useSelector(getIssueById);
  // Can't use useMemo inside the callback (rules of hooks), so using ref instead
  const memoizedIssueRef =
    useRef<{
      issueId: string | undefined;
      json: string;
      issue: Issue | undefined;
    } | null>(null);

  return useCallback(
    (issueId: string | undefined): Issue | undefined => {
      const issue = getIssue(issueId);
      const issueJson = JSON.stringify(issue);

      const cached = memoizedIssueRef.current;
      if (cached && cached.issueId === issueId && cached.json === issueJson) {
        return cached.issue;
      }

      memoizedIssueRef.current = { issueId, json: issueJson, issue };
      return issue;
    },
    [getIssue]
  );
};
